import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Link } from "react-router-dom";

/**
 * SMS Consent Modal — Twilio opt-in compliance.
 *
 * Shown the first time a user enables SMS alerts to their Trusted Circle.
 * Per Twilio messaging consent guidelines the user must affirmatively
 * acknowledge:
 *   • what messages will be sent
 *   • when they will be sent
 *   • that the trusted contact agreed to receive them
 *   • message frequency, data rates, STOP / HELP instructions
 *
 * On confirm we PATCH /me/preferences with `sms_consent=true` and
 * `sms_consent_ack_at=<ISO now>`. The backend clears the acknowledgement
 * automatically when the user removes their last trusted contact.
 */
export default function SmsConsentModal({ open, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal re-opens so the checkbox isn't
  // pre-checked from a previous session.
  useEffect(() => {
    if (open) {
      setAgreed(false);
      setSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent
        className="rounded-3xl max-w-md p-0 overflow-hidden"
        data-testid="sms-consent-modal"
        aria-describedby="sms-consent-body"
      >
        <div className="px-6 pt-6 pb-3">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl leading-tight text-left">
              {t("sms_consent.h")}
            </DialogTitle>
          </DialogHeader>

          <DialogDescription
            id="sms-consent-body"
            className="mt-3 text-sm text-foreground/80 leading-relaxed text-left"
          >
            {t("sms_consent.intro")}
          </DialogDescription>

          <ul className="mt-3 space-y-1.5 text-sm text-foreground/80 leading-relaxed">
            <li className="flex gap-2"><span className="text-brand">•</span>{t("sms_consent.bullet_missed")}</li>
            <li className="flex gap-2"><span className="text-brand">•</span>{t("sms_consent.bullet_need_help")}</li>
            <li className="flex gap-2"><span className="text-brand">•</span>{t("sms_consent.bullet_other")}</li>
          </ul>

          <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
            {t("sms_consent.only_when")}
          </p>
          <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
            {t("sms_consent.please_confirm")}
          </p>
        </div>

        <div className="px-6 py-4 bg-brand-soft/30 border-t border-brand/10">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="sms-consent-checkbox"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5"
            />
            <span className="text-sm text-foreground/85 leading-snug">
              {t("sms_consent.checkbox_label")}
            </span>
          </label>
        </div>

        <div className="px-6 pt-4 pb-5 space-y-2 text-[11px] text-foreground/60 leading-relaxed">
          <p>{t("sms_consent.compliance_frequency")}</p>
          <p>{t("sms_consent.compliance_rates")}</p>
          <p>{t("sms_consent.compliance_stop")}</p>
          <p>{t("sms_consent.compliance_help")}</p>
          <p>
            <Link
              to="/privacy"
              className="underline underline-offset-2 hover:text-brand"
              data-testid="sms-consent-privacy"
            >
              {t("sms_consent.compliance_privacy")}
            </Link>
            {" · "}
            <Link
              to="/terms"
              className="underline underline-offset-2 hover:text-brand"
              data-testid="sms-consent-terms"
            >
              {t("sms_consent.compliance_terms")}
            </Link>
          </p>
        </div>

        <DialogFooter className="px-6 pb-6 pt-0 gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
          <Button
            data-testid="sms-consent-cancel"
            variant="outline"
            className="rounded-full"
            onClick={onCancel}
            disabled={submitting}
          >
            {t("sms_consent.cancel")}
          </Button>
          <Button
            data-testid="sms-consent-confirm"
            className="rounded-full bg-brand text-white hover:bg-brand/90"
            disabled={!agreed || submitting}
            onClick={handleConfirm}
          >
            {submitting ? t("sms_consent.enabling") : t("sms_consent.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
